import { Overflow, resolveOverflow, ThemeColor } from 'config/styled-components-styles';
import styled, { css } from 'styled-components';

const DEFAULT_BACKGROUND_COLOR: ThemeColor = 'transparent';
const DEFAULT_BACKGROUND_IMAGE = 'none';
const DEFAULT_MARGIN = 'unset';
const DEFAULT_PADDING = 'unset';
const DEFAULT_BORDER = 'none';
const DEFAULT_BORDER_WIDTH = '1px';
const DEFAULT_BORDER_RADIUS = '0px';
const DEFAULT_WIDTH = 'auto';
const DEFAULT_HEIGHT = 'auto';
const DEFAULT_FLEX_GROW = 'unset';
const DEFAULT_FLEX_SHRINK = 'unset';
const DEFAULT_JUSTIFY_SELF = 'unset';
const DEFAULT_BOX_SHADOW = 'none';
const DEFAULT_POSITION = 'static';
const DEFAULT_CURSOR = 'unset';
const DEFAULT_OUTLINE = 'unset';

export type BlockProps = {
    $display?: 'block' | 'inline' | 'inline-block' | 'none';
    $flex?: 'auto' | number | string;

    $width?: string;
    $height?: string;
    $minHeight?: string;
    $minWidth?: string;
    $maxHeight?: string;
    $maxWidth?: string;
    $margin?: string;
    $padding?: string;
    $backgroundColor?: ThemeColor;
    $backgroundHexColor?: string;
    $backgroundImageUrl?: string;
    $backgroundPosition?: string;
    $backgroundRepeat?: string;
    $backgroundSize?: string;
    $borderColor?: ThemeColor;
    $borderWidth?: string;
    $borderTop?: string;
    $borderRight?: string;
    $borderBottom?: string;
    $borderLeft?: string;
    $borderRadius?: string;
    $borderStyle?: string;
    $outline?: string;
    $overflow?: Overflow | [Overflow, Overflow];
    $boxShadow?: string;
    $position?: 'static' | 'relative' | 'absolute' | 'fixed' | 'sticky';
    $top?: number;
    $bottom?: number;
    $left?: number;
    $right?: number;
    $cursor?: string;
    $textTransform?: string;
    $zIndex?: number;
    $userSelect?: 'auto' | 'none';
    $flexGrow?: string;
    $justifySelf?: 'start' | 'end' | 'center';
    $gridColumnStart?: number;
    $gridColumnEnd?: number;
    $gridRowStart?: number;
    $gridRowEnd?: number;
    $transition?: string[];
    $transform?: string[];
    $scrollbarThumbBackground?: ThemeColor;
    $scrollbarThumbBackgroundHover?: ThemeColor;
};

type FlexPositionContent =
    'flex-start' | 'flex-end' | 'center' | 'stretch' | 'space-around' | 'space-between' | 'space-evenly';

export type FlexProps = {
    $display: 'flex' | 'inline-flex';
    $justifyContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around';
    $alignItems?: 'stretch' | 'flex-start' | 'flex-end' | 'center' | 'baseline';
    $flexDirection?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
    $gap?: number | string;
    $flexWrap?: 'wrap' | 'wrap-reverse' | 'nowrap';
    $flex?: 'auto' | number | string;
    $alignContent?: FlexPositionContent;
    $placeSelf?: 'auto' | 'start' | 'end' | 'center' | 'stretch' | 'space-around' | 'space-between' | 'space-evenly';
    $width?: string;
    $height?: string;
    $minHeight?: string;
    $minWidth?: string;
    $maxHeight?: string;
    $maxWidth?: string;
    $margin?: string;
    $padding?: string;
    $backgroundColor?: ThemeColor;
    $backgroundHexColor?: string;
    $backgroundImageUrl?: string;
    $backgroundPosition?: string;
    $backgroundRepeat?: string;
    $backgroundSize?: string;
    $borderColor?: ThemeColor;
    $borderWidth?: string;
    $borderTop?: string;
    $borderRight?: string;
    $borderBottom?: string;
    $borderLeft?: string;
    $borderRadius?: string;
    $borderStyle?: string;
    $outline?: string;
    $overflow?: Overflow | [Overflow, Overflow];
    $boxShadow?: string;
    $position?: 'static' | 'relative' | 'absolute' | 'fixed' | 'sticky';
    $top?: number;
    $bottom?: number;
    $left?: number;
    $right?: number;
    $cursor?: string;
    $textTransform?: string;
    $zIndex?: number;
    $userSelect?: 'auto' | 'none';
    $flexGrow?: string;
    $justifySelf?: 'start' | 'end' | 'center';
    $gridColumnStart?: number;
    $gridColumnEnd?: number;
    $gridRowStart?: number;
    $gridRowEnd?: number;
    $transition?: string[];
    $transform?: string[];
    $scrollbarThumbBackground?: ThemeColor;
    $scrollbarThumbBackgroundHover?: ThemeColor;
};

type PositionContent = 'normal' | 'start' | 'end' | 'center' | 'stretch';
type ContentDistribution = 'space-around' | 'space-between' | 'space-evenly';
type PlaceContent = PositionContent | ContentDistribution;
type ItemPositionContent = PositionContent;

export type GridProps = {
    $display: 'grid';
    $gridTemplateColumns?: string;
    $gridAutoColumns?: string;
    $gridAutoFlow?: string;
    $gridTemplateRows?: string;
    $gridGap?: string;
    $gridTemplateAreas?: string;
    $alignItems?: 'start' | 'end' | 'center' | 'stretch';
    $justifyItems?: 'start' | 'end' | 'center' | 'stretch';
    $justifyContent?: PlaceContent;
    $alignContent?: PlaceContent;
    $placeContent?: PlaceContent | `${PlaceContent} ${PlaceContent}`;
    $placeItems?: ItemPositionContent | `${ItemPositionContent} ${ItemPositionContent}`;
    $flex?: 'auto' | number | string;
    $width?: string;
    $height?: string;
    $minHeight?: string;
    $minWidth?: string;
    $maxHeight?: string;
    $maxWidth?: string;
    $margin?: string;
    $padding?: string;
    $backgroundColor?: ThemeColor;
    $backgroundHexColor?: string;
    $backgroundImageUrl?: string;
    $backgroundPosition?: string;
    $backgroundRepeat?: string;
    $backgroundSize?: string;
    $borderColor?: ThemeColor;
    $borderWidth?: string;
    $borderTop?: string;
    $borderRight?: string;
    $borderBottom?: string;
    $borderLeft?: string;
    $borderRadius?: string;
    $borderStyle?: string;
    $outline?: string;
    $overflow?: Overflow | [Overflow, Overflow];
    $boxShadow?: string;
    $position?: 'static' | 'relative' | 'absolute' | 'fixed' | 'sticky';
    $top?: number;
    $bottom?: number;
    $left?: number;
    $right?: number;
    $cursor?: string;
    $textTransform?: string;
    $zIndex?: number;
    $userSelect?: 'auto' | 'none';
    $flexGrow?: string;
    $justifySelf?: 'start' | 'end' | 'center';
    $gridColumnStart?: number;
    $gridColumnEnd?: number;
    $gridRowStart?: number;
    $gridRowEnd?: number;
    $transition?: string[];
    $transform?: string[];
    $scrollbarThumbBackground?: ThemeColor;
    $scrollbarThumbBackgroundHover?: ThemeColor;
};

export type ContainerProps = (BlockProps | FlexProps | GridProps) & {
    $visibility?: 'visible' | 'hidden';
    $flexShrink?: string;
    $alignSelf?: 'auto' | 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch';
};

const flexMixin = css<FlexProps>`
    justify-content: ${(props): string => props.$justifyContent ?? 'flex-start'};
    align-items: ${(props): string => props.$alignItems ?? 'stretch'};
    flex-direction: ${(props): string => props.$flexDirection ?? 'row'};
    gap: ${(props): string => (typeof props.$gap === 'number' ? `${props.$gap}px` : (props.$gap ?? '0px'))};
    flex-wrap: ${(props): string => props.$flexWrap ?? 'nowrap'};
    align-content: ${(props): string => props.$alignContent ?? 'stretch'};
`;

const gridMixin = css<GridProps>`
    grid-template-columns: ${(props): string => props.$gridTemplateColumns ?? 'unset'};
    grid-auto-flow: ${(props): string => props.$gridAutoFlow ?? 'unset'};
    grid-auto-columns: ${(props): string => props.$gridAutoColumns ?? 'unset'};
    grid-template-rows: ${(props): string => props.$gridTemplateRows ?? 'unset'};
    grid-gap: ${(props): string => props.$gridGap ?? 'unset'};
    grid-template-areas: ${(props): string => props.$gridTemplateAreas ?? 'unset'};
    align-items: ${(props): string => props.$alignItems ?? 'stretch'};
    justify-items: ${(props): string => props.$justifyItems ?? 'stretch'};
    justify-content: ${(props): string => props.$justifyContent ?? 'stretch'};
    align-content: ${(props): string => props.$alignContent ?? 'stretch'};
    place-content: ${(props): string => props.$placeContent ?? 'unset'};
    place-items: ${(props): string | undefined => props.$placeItems ?? undefined};
`;

export const Container = styled.div<ContainerProps>`
    display: ${(props): string => props.$display ?? 'block'};
    visibility: ${(props): string => props.$visibility ?? 'visible'};
    ${(props): any => (props.$display === 'flex' ? flexMixin : css``)};
    ${(props): any => (props.$display === 'grid' ? gridMixin : css``)};

    top: ${(props): string => (props.$top !== undefined ? `${props.$top}px` : 'unset')};
    bottom: ${(props): string => (props.$bottom !== undefined ? `${props.$bottom}px` : 'unset')};
    left: ${(props): string => (props.$left !== undefined ? `${props.$left}px` : 'unset')};
    right: ${(props): string => (props.$right !== undefined ? `${props.$right}px` : 'unset')};
    background-color: ${props =>
        props.$backgroundHexColor || `var(--${props.$backgroundColor ?? DEFAULT_BACKGROUND_COLOR})`};
    background-image: ${(props): string =>
        props.$backgroundImageUrl ? `url(${props.$backgroundImageUrl})` : DEFAULT_BACKGROUND_IMAGE};
    background-position: ${(props): string => props.$backgroundPosition ?? 'unset'};
    background-repeat: ${(props): string => props.$backgroundRepeat ?? 'unset'};
    background-size: ${(props): string => props.$backgroundSize ?? 'unset'};
    margin: ${(props): string => props.$margin ?? DEFAULT_MARGIN};
    padding: ${(props): string => props.$padding ?? DEFAULT_PADDING};
    border: ${(props): string =>
        props.$borderColor
            ? `solid ${props.$borderWidth ?? DEFAULT_BORDER_WIDTH} var(--${props.$borderColor})`
            : DEFAULT_BORDER};
    border-top: ${(props): string | undefined => props.$borderTop};
    border-right: ${(props): string | undefined => props.$borderRight};
    border-bottom: ${(props): string | undefined => props.$borderBottom};
    border-left: ${(props): string | undefined => props.$borderLeft};
    border-radius: ${(props): string => props.$borderRadius ?? DEFAULT_BORDER_RADIUS};
    ${(props): string => (props.$borderStyle ? `border-style: ${props.$borderStyle};` : '')}
    outline: ${(props): string => props.$outline ?? DEFAULT_OUTLINE};
    width: ${(props): string => props.$width ?? DEFAULT_WIDTH};
    min-width: ${(props): string => props.$minWidth ?? 'unset'};
    max-width: ${(props): string => props.$maxWidth ?? 'unset'};
    height: ${(props): string => props.$height ?? DEFAULT_HEIGHT};
    min-height: ${(props): string => props.$minHeight ?? 'unset'};
    max-height: ${(props): string => props.$maxHeight ?? 'unset'};
    overflow: ${(props): string => resolveOverflow(props.$overflow ?? 'visible')};
    box-shadow: ${(props): string => props.$boxShadow ?? DEFAULT_BOX_SHADOW};
    position: ${(props): string => props.$position || DEFAULT_POSITION};
    cursor: ${(props): string => props.$cursor || (props.onClick ? 'pointer' : DEFAULT_CURSOR)};
    text-transform: ${(props): string => props.$textTransform ?? 'unset'};
    z-index: ${(props): number | string => props.$zIndex ?? 'auto'};
    user-select: ${(props): string => props.$userSelect ?? 'auto'};
    flex-grow: ${(props): string => props.$flexGrow ?? DEFAULT_FLEX_GROW};
    flex-shrink: ${(props): string => props.$flexShrink ?? DEFAULT_FLEX_SHRINK};
    justify-self: ${(props): string => props.$justifySelf ?? DEFAULT_JUSTIFY_SELF};
    grid-column-start: ${(props): string | number => props.$gridColumnStart ?? 'unset'};
    grid-column-end: ${(props): string | number => props.$gridColumnEnd ?? 'unset'};
    grid-row-start: ${(props): string | number => props.$gridRowStart ?? 'unset'};
    grid-row-end: ${(props): string | number => props.$gridRowEnd ?? 'unset'};
    flex: ${(props): FlexProps['$flex'] => props.$flex || undefined};
    transform: ${(props): string => (props.$transform?.length ? `${props.$transform.join(',')}` : '')};
    align-self: ${(props): string | undefined => props.$alignSelf || undefined};

    transition:
        ${(props): string => (props.$transition?.length ? `${props.$transition.join(',')},` : '')} background-color
            var(--animationTime),
        grid-template-columns var(--animationTime);

    &::-webkit-scrollbar {
        width: 4px;
        height: 4px;
        position: absolute;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: var(--${(props): string => props.$scrollbarThumbBackground || 'manatee'});
        border-radius: 2px;
        width: 4px;
        height: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: var(--${(props): string => props.$scrollbarThumbBackgroundHover || 'stormGray'});
    }
`;
